import { Action, Reducer } from 'redux';
import {
  AvailableMedicalServiceState,
  AvailableMedicalServicesUnloadState,
  MedicalServicesPageState,
  MedicalServicesPageUnloadState
} from '../states/MedicalServicesState';
import { AvailableMedicalService } from '../../models/Entities/MedicalServices/AvailableMedicalService';
import { MedicalServiceSpecialty } from '../../models/Entities/MedicalServices/MedicalServiceSpecialty';

// functions
import * as Functions from '../../functions/common';
import { MedicalServicesPageQueryPayload } from '../actions/MedicalServicesActions';

const flag = (value: boolean | undefined): string => {
  if (value === undefined) return '';
  return value ? '1' : '0';
};

/** Misma clave que arma el listado (MedicalServiceList.Logic.queryKey). */
export const pageQueryKey = (query: MedicalServicesPageQueryPayload): string =>
  [query.pageNumber, query.pageSize, query.searchText ?? '', flag(query.available), flag(query.firstLevelOnly), query.parentServiceId ?? '', query.categoryId ?? ''].join('|');

/** Aplica un cambio local a la página recibida (borrado, edición, estado) y ajusta el total. */
const patchPage = (
  page: MedicalServicesPageState | undefined,
  update: (items: AvailableMedicalService[]) => AvailableMedicalService[],
  totalDelta: number = 0
): MedicalServicesPageState => {
  const current = page ?? MedicalServicesPageUnloadState;
  const items = update(current.items || []);
  const totalRecords = Math.max(0, current.totalRecords + (items.length !== (current.items || []).length ? totalDelta : 0));
  return { ...current, items, totalRecords, totalPages: current.pageSize > 0 ? Math.ceil(totalRecords / current.pageSize) : current.totalPages };
};

// actions
import * as Actions from '../actions';

type KnownAction =
  | Actions.AvailableMedicalServices.AvailableMedicalServiceGetAll_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceGetAll_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceGetAll_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServicePage_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServicePage_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServicePage_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceUpdateAll_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceUpdateAll_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceUpdateAll_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceGetById_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceGetById_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceGetById_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceAdd_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceAdd_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceAdd_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceUpdate_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceUpdate_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceUpdate_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceDelete_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceDelete_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceDelete_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceChangeStatus_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceChangeStatus_Receive_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceChangeStatus_Fail_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceChangeSettings_Request_Action
  | Actions.AvailableMedicalServices.AvailableMedicalServiceChangeSettings_Fail_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyGetAll_Request_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyGetAll_Receive_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyGetAll_Fail_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyAdd_Request_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyAdd_Receive_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyAdd_Fail_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyRemove_Request_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyRemove_Receive_Action
  | Actions.AvailableMedicalServices.MedicalServiceSpecialtyRemove_Fail_Action;

export const MedicalServicesReducer: Reducer<AvailableMedicalServiceState> = (
  state: AvailableMedicalServiceState | undefined,
  incomingAction: Action
): AvailableMedicalServiceState => {
  if (state === undefined) {
    return AvailableMedicalServicesUnloadState as AvailableMedicalServiceState;
  }

  const action = incomingAction as KnownAction;

  switch (action.type) {
    case 'AVAILABLE_MEDICAL_SERVICE_GET_ALL_REQUEST_ACTION':
      return {
        ...state,
        isLoadingAll: true,
        successLoadingAll: undefined,
        failOnLoadingAll: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_GET_ALL_RECEIVE_ACTION':
      return {
        ...state,
        isLoadingAll: false,
        successLoadingAll: true,
        failOnLoadingAll: false,
        error: undefined,
        list: [...action.Services].sort(
          Functions.DynamicSort('serviceNameReference')
        )
      };
    case 'AVAILABLE_MEDICAL_SERVICE_GET_ALL_FAIL_ACTION':
      return {
        ...state,
        isLoadingAll: false,
        successLoadingAll: false,
        failOnLoadingAll: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_UPDATE_ALL_REQUEST_ACTION':
      return {
        ...state,
        isUpdatingAll: true,
        isUpdatingAllSuccess: undefined,
        FailUpdatingAll: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_UPDATE_ALL_RECEIVE_ACTION':
      return {
        ...state,
        isUpdatingAll: false,
        isUpdatingAllSuccess: true,
        FailUpdatingAll: false,
        error: undefined,
        list: [...action.Services].sort(
          Functions.DynamicSort('serviceNameReference')
        )
      };
    case 'AVAILABLE_MEDICAL_SERVICE_UPDATE_ALL_FAIL_ACTION':
      return {
        ...state,
        isUpdatingAll: false,
        isUpdatingAllSuccess: false,
        FailUpdatingAll: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_DELETE_REQUEST_ACTION':
      return {
        ...state,
        isDeletingOne: true,
        successDeletingOne: undefined,
        failOnDeletingOne: false,
        error: undefined
      };
    // Página del listado (búsqueda + paginado en servidor). `page.items` es la página
    // recibida; `list` sigue siendo el catálogo completo de los selectores.
    case 'AVAILABLE_MEDICAL_SERVICE_PAGE_REQUEST_ACTION':
      return {
        ...state,
        page: {
          ...(state.page ?? MedicalServicesPageUnloadState),
          isLoading: true,
          failed: false,
          error: undefined,
          queryKey: pageQueryKey(action.query)
        }
      };
    case 'AVAILABLE_MEDICAL_SERVICE_PAGE_RECEIVE_ACTION': {
      const current = state.page ?? MedicalServicesPageUnloadState;
      // Llegó la respuesta de una consulta vieja: se ignora, la vigente está en vuelo.
      if (current.queryKey && current.queryKey !== pageQueryKey(action.query)) return state;
      return {
        ...state,
        page: {
          items: action.items,
          pageNumber: action.query.pageNumber,
          pageSize: action.query.pageSize,
          searchText: action.query.searchText,
          available: action.query.available,
          totalRecords: action.totalRecords,
          totalPages: action.totalPages,
          queryKey: pageQueryKey(action.query),
          isLoading: false,
          loaded: true,
          failed: false,
          error: undefined
        }
      };
    }
    case 'AVAILABLE_MEDICAL_SERVICE_PAGE_FAIL_ACTION': {
      const current = state.page ?? MedicalServicesPageUnloadState;
      if (current.queryKey && current.queryKey !== pageQueryKey(action.query)) return state;
      return {
        ...state,
        page: { ...current, isLoading: false, failed: true, error: action.error }
      };
    }
    case 'AVAILABLE_MEDICAL_SERVICE_DELETE_RECEIVE_ACTION':
      return {
        ...state,
        isDeletingOne: false,
        successDeletingOne: true,
        failOnDeletingOne: false,
        error: undefined,
        list: state.list.filter(
          (item: AvailableMedicalService) =>
            item.id !== action.AvailableMedicalServiceId
        ),
        page: patchPage(state.page, (items) => items.filter((item) => item.id !== action.AvailableMedicalServiceId), -1)
      };
    case 'AVAILABLE_MEDICAL_SERVICE_DELETE_FAIL_ACTION':
      return {
        ...state,
        isDeletingOne: false,
        successDeletingOne: false,
        failOnDeletingOne: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_GET_BY_ID_REQUEST_ACTION':
      return {
        ...state,
        isLoadingOne: true,
        successLoadingOne: undefined,
        failOnLoadingOne: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_GET_BY_ID_RECEIVE_ACTION':
      return {
        ...state,
        isLoadingOne: false,
        successLoadingOne: true,
        failOnLoadingOne: false,
        error: undefined,
        list: [
          ...(state.list.filter(
            (item: AvailableMedicalService) =>
              item.id !== action.AvailableMedicalService.id
          ) || []),
          action.AvailableMedicalService
        ]
      };
    case 'AVAILABLE_MEDICAL_SERVICE_GET_BY_ID_FAIL_ACTION':
      return {
        ...state,
        isLoadingOne: false,
        successLoadingOne: false,
        failOnLoadingOne: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_ADD_REQUEST_ACTION':
      return {
        ...state,
        isAddingNewOne: true,
        successAddingNewOne: undefined,
        failOnAddingNewOne: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_ADD_RECEIVE_ACTION':
      return {
        ...state,
        isAddingNewOne: false,
        successAddingNewOne: true,
        failOnAddingNewOne: false,
        error: undefined,
        list: [...state.list, action.AvailableMedicalService].sort(
          Functions.DynamicSort('serviceNameReference')
        )
      };
    case 'AVAILABLE_MEDICAL_SERVICE_ADD_FAIL_ACTION':
      return {
        ...state,
        isAddingNewOne: false,
        successAddingNewOne: false,
        failOnAddingNewOne: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_UPDATE_REQUEST_ACTION':
      return {
        ...state,
        isUpdatingOne: true,
        successUpdatingOne: undefined,
        failOnUpdatingOne: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_UPDATE_RECEIVE_ACTION':
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: true,
        failOnUpdatingOne: false,
        error: undefined,
        list: [
          ...state.list.filter(
            (item: AvailableMedicalService) =>
              item.id !== action.AvailableMedicalService.id
          ),
          action.AvailableMedicalService
        ].sort(Functions.DynamicSort('serviceNameReference')),
        page: patchPage(state.page, (items) =>
          items.map((item) => (item.id === action.AvailableMedicalService.id ? { ...item, ...action.AvailableMedicalService } : item))
        )
      };
    case 'AVAILABLE_MEDICAL_SERVICE_UPDATE_FAIL_ACTION':
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: false,
        failOnUpdatingOne: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_CHANGE_STATUS_REQUEST_ACTION':
      return {
        ...state,
        isChangingStatus: true,
        successChangingStatus: undefined,
        failOnChangingStatus: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_CHANGE_STATUS_RECEIVE_ACTION':
      return {
        ...state,
        isChangingStatus: false,
        successChangingStatus: true,
        failOnChangingStatus: false,
        error: undefined,
        list: [
          ...state.list.filter(
            (item: AvailableMedicalService) =>
              item.id !== action.medicalServiceId
          ),
          {
            ...state.list.find(
              (item: AvailableMedicalService) =>
                item.id === action.medicalServiceId
            ),
            available: action.status
          } as AvailableMedicalService
        ].sort(Functions.DynamicSort('serviceNameReference')),
        page: patchPage(state.page, (items) =>
          items.map((item) => (item.id === action.medicalServiceId ? { ...item, available: action.status } : item))
        )
      };
    case 'AVAILABLE_MEDICAL_SERVICE_CHANGE_STATUS_FAIL_ACTION':
      return {
        ...state,
        isChangingStatus: false,
        successChangingStatus: false,
        failOnChangingStatus: true,
        error: action.error
      };
    case 'AVAILABLE_MEDICAL_SERVICE_CHANGE_SETTINGS_REQUEST_ACTION':
      return {
        ...state,
        isUpdatingOne: true,
        successUpdatingOne: undefined,
        failOnUpdatingOne: false,
        error: undefined
      };
    case 'AVAILABLE_MEDICAL_SERVICE_CHANGE_SETTINGS_FAIL_ACTION':
      return {
        ...state,
        isUpdatingOne: false,
        successUpdatingOne: false,
        failOnUpdatingOne: true,
        error: action.error
      };
    case 'MEDICAL_SERVICE_SPECIALTY_GET_ALL_REQUEST_ACTION':
      return { ...state, isLoadingSpecialties: true, failOnLoadingSpecialties: false, errorSpecialties: undefined };
    // Las especialidades se aplican en `list` y en `page.items`: el listado carga los servicios paginados
    // y el formulario los lee de ahí, así que si sólo se tocara `list` no se verían nunca.
    case 'MEDICAL_SERVICE_SPECIALTY_GET_ALL_RECEIVE_ACTION': {
      const withSpecialties = (items: AvailableMedicalService[]) =>
        items.map((item: AvailableMedicalService) =>
          item.id === action.medicalServiceId
            ? { ...item, specialties: action.specialties }
            : item
        );
      return {
        ...state,
        isLoadingSpecialties: false,
        failOnLoadingSpecialties: false,
        list: withSpecialties(state.list),
        page: patchPage(state.page, withSpecialties)
      };
    }
    case 'MEDICAL_SERVICE_SPECIALTY_GET_ALL_FAIL_ACTION':
      return { ...state, isLoadingSpecialties: false, failOnLoadingSpecialties: true, errorSpecialties: action.error };
    case 'MEDICAL_SERVICE_SPECIALTY_ADD_REQUEST_ACTION':
      return { ...state, isAddingSpecialty: true, failOnAddingSpecialty: false, errorSpecialties: undefined };
    case 'MEDICAL_SERVICE_SPECIALTY_ADD_RECEIVE_ACTION': {
      const newSpecialty: MedicalServiceSpecialty = {
        id: action.associationId,
        medicalServiceId: action.medicalServiceId,
        specialtyId: action.specialtyId,
        specialtyName: action.specialtyName
      };
      const withAdded = (items: AvailableMedicalService[]) =>
        items.map((item: AvailableMedicalService) =>
          item.id === action.medicalServiceId
            ? { ...item, specialties: [...(item.specialties ?? []), newSpecialty] }
            : item
        );
      return {
        ...state,
        isAddingSpecialty: false,
        failOnAddingSpecialty: false,
        list: withAdded(state.list),
        page: patchPage(state.page, withAdded)
      };
    }
    case 'MEDICAL_SERVICE_SPECIALTY_ADD_FAIL_ACTION':
      return { ...state, isAddingSpecialty: false, failOnAddingSpecialty: true, errorSpecialties: action.error };
    case 'MEDICAL_SERVICE_SPECIALTY_REMOVE_REQUEST_ACTION':
      return { ...state, isRemovingSpecialty: true, failOnRemovingSpecialty: false, errorSpecialties: undefined };
    case 'MEDICAL_SERVICE_SPECIALTY_REMOVE_RECEIVE_ACTION': {
      const withRemoved = (items: AvailableMedicalService[]) =>
        items.map((item: AvailableMedicalService) =>
          item.id === action.medicalServiceId
            ? {
                ...item,
                specialties: (item.specialties ?? []).filter(
                  (s: MedicalServiceSpecialty) => s.id !== action.associationId
                )
              }
            : item
        );
      return {
        ...state,
        isRemovingSpecialty: false,
        failOnRemovingSpecialty: false,
        list: withRemoved(state.list),
        page: patchPage(state.page, withRemoved)
      };
    }
    case 'MEDICAL_SERVICE_SPECIALTY_REMOVE_FAIL_ACTION':
      return { ...state, isRemovingSpecialty: false, failOnRemovingSpecialty: true, errorSpecialties: action.error };
  }

  return state;
};
